import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Contact } from "@/components/Contact";
import { SEO } from "@/components/SEO";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Button } from "@/components/ui/button";
import { ArrowRight, Phone, Trash2, Thermometer, LayoutGrid, Paintbrush, Layers, Check, Shield } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const steps = [
  {
    icon: Trash2,
    number: "01",
    title: "Vorbereitung & Entkernung",
    text: "Jede gute Sanierung beginnt mit einer sauberen Basis. Wir entfernen Altlasten wie tapetenähnliche Beläge, alte Bodenbeläge oder marode Putze fachgerecht.",
    detail: "Staubarme Demontage & Entsorgung."
  },
  {
    icon: Thermometer,
    number: "02",
    title: "Energetik & Klima",
    text: "Wir machen Ihre Räume effizient. Bevor die Optik kommt, sorgen wir für die thermische Hülle.",
    detail: "Innendämmung (Calciumsilikat/Holzfaser), Dachboden- und Kellerdeckendämmung. Ziel: Senkung der Heizkosten & Vermeidung von Schimmelbildung."
  },
  {
    icon: LayoutGrid,
    number: "03",
    title: "Trockenbau & Grundrissoptimierung",
    text: "Wir schaffen neue Räume. Ob abgehängte Decken mit moderner LED-Beleuchtung oder neue Trennwände für eine bessere Raumaufteilung.",
    detail: "Hochwertige Trockenbauarbeiten & Akustiklösungen."
  },
  {
    icon: Paintbrush,
    number: "04",
    title: "High-End Oberflächenfinish",
    text: "Jetzt wird es schön. Wir bereiten Wände in Q3 oder Q4 Qualität vor – die Basis für exklusive Designs.",
    detail: "Glattspachtel, edle Tapeten oder unsere Exklusiven Oberflächen (Betonoptik/Metalloptik)."
  },
  {
    icon: Layers,
    number: "05",
    title: "Boden-Komplettservice",
    text: "Vom Estrich bis zum Designboden. Ein neuer Boden verändert die gesamte Raumwirkung.",
    detail: "Design-Vinyl, Parkett oder fugenlose Mikrozement-Böden."
  }
];

const checklist = [
  { title: "Feuchtigkeitscheck", text: "Sind die Wände trocken genug für die Dämmung?" },
  { title: "Untergrundprüfung", text: "Hält der alte Putz die neuen Spachtelschichten?" },
  { title: "Energetisches Konzept", text: "Wo geht die meiste Wärme verloren?" },
  { title: "Design-Konzept", text: "Welche Farben und Böden harmonieren mit Ihrem Lichtkonzept?" },
];

const faqs = [
  {
    question: "Muss ich während der Sanierung ausziehen?",
    answer: "Das hängt vom Umfang ab. Bei einer etappenweisen Sanierung ist das Wohnen oft möglich. Wir arbeiten extrem staubarm mit modernster Absaugtechnik."
  },
  {
    question: "Können Sie auch das Bad fugenlos sanieren?",
    answer: "Ja, das ist eine unserer Spezialitäten. Wir integrieren die Badsanierung nahtlos in das Gesamtkonzept Ihrer Innensanierung."
  },
  {
    question: "Wie lange dauert eine Kernsanierung einer Wohnung?",
    answer: "In der Regel planen wir je nach Größe 4 bis 8 Wochen für eine umfassende Sanierung ein."
  }
];

const Innenarbeiten = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Innensanierung & Komplettmodernisierung",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Malerfachbetrieb Stein GmbH und Co. KG",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Mindener Str. 183",
        "addressLocality": "Herford",
        "postalCode": "32049",
        "addressRegion": "NRW",
        "addressCountry": "DE"
      },
      "telephone": "+49-5221-3832130"
    },
    "areaServed": [
      { "@type": "City", "name": "Herford" },
      { "@type": "City", "name": "Hiddenhausen" },
      { "@type": "City", "name": "Löhne" }
    ],
    "description": "Komplette Innensanierung in Herford. Von Entkernung über Dämmung bis zum Design-Finish. Alles aus einer Hand vom Malermeister."
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Innensanierung in Herford: Renovierung aus einer Hand"
        description="Innensanierung in Herford: Von Entkernung über Dämmung bis Design-Finish – alles aus einer Hand vom Malermeister. Termintreu, sauber, Festpreis. ☎ 05221 3832130"
        keywords="Innensanierung Herford, Altbausanierung Herford, Komplettrenovierung Herford, Wohnung sanieren Herford, Trockenbau Herford, Spachteltechnik Q4 Herford"
        canonical="https://malerfachbetrieb-stein.de/innensanierung-herford"
        structuredData={structuredData}
      />
      <Navigation />
      <WhatsAppButton />
      
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-foreground overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 right-10 w-72 h-72 border border-background/5 rotate-45" />
            <div className="absolute bottom-10 left-20 w-96 h-96 border border-background/5 rounded-full" />
          </div>
          <div className="container mx-auto relative z-10">
            <div className="max-w-3xl">
              <span className="inline-block text-sm font-semibold tracking-wider uppercase text-background/50 mb-4">
                Innensanierung & Komplettmodernisierung
              </span>
              <h1 className="font-caslon text-3xl md:text-4xl lg:text-5xl lg:text-6xl font-bold text-background mb-6 leading-tight">
                Innensanierung aus Meisterhand: Ihr Zuhause in Herford neu gedacht
              </h1>
              <p className="text-lg md:text-xl text-background/80 mb-10 leading-relaxed max-w-2xl">
                Wir koordinieren Ihr Sanierungsprojekt von der ersten Planung bis zum Einzug. 
                Energetisch optimiert, modern gestaltet und fachgerecht ausgeführt.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-background text-foreground hover:bg-background/90 font-semibold px-10 py-7 text-base rounded-2xl transition-all duration-500 hover:scale-105 group"
                  onClick={() => document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Jetzt unverbindliches Sanierungsgespräch vereinbaren
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
                <a href="tel:+4952213832130">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-background text-background hover:bg-background/10 font-semibold px-10 py-7 text-base rounded-2xl">
                    <Phone className="mr-2 h-5 w-5" />
                    05221 / 3832130
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Alles aus einer Hand */}
        <section className="py-16 md:py-24 lg:py-32 bg-background">
          <div className="container mx-auto max-w-4xl">
            <h2 className="font-caslon text-3xl md:text-4xl font-bold text-foreground mb-8">
              Stressfrei sanieren: Ihr Sanierungsprojekt ohne Schnittstellenverluste
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Eine Innensanierung ist ein komplexes Zusammenspiel vieler Fachbereiche. Wer koordiniert 
              den Trockenbau? Wer sorgt für die rissfreie Verspachtelung? Wer dämmt die Außenwände von 
              innen? Die Antwort: Wir. Als Ihr Malermeister in Herford übernehmen wir die Regie für 
              Ihre Innenräume. Sie haben nur einen Ansprechpartner und die Gewissheit, dass alle 
              Arbeiten perfekt ineinandergreifen.
            </p>
          </div>
        </section>

        {/* Sanierungsschritte */}
        <section className="py-16 md:py-24 lg:py-32 bg-muted/30">
          <div className="container mx-auto">
            <div className="max-w-3xl mb-16">
              <span className="text-sm font-semibold tracking-wider uppercase text-muted-foreground mb-4 block">
                Unser Prozess
              </span>
              <h2 className="font-caslon text-3xl md:text-4xl font-bold text-foreground">
                Unsere Sanierungsschritte für Ihre Immobilie
              </h2>
            </div>

            <div className="space-y-6">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={index} className="bg-background rounded-3xl p-8 md:p-10 flex flex-col md:flex-row gap-6 md:gap-10 items-start">
                    <div className="flex items-center gap-6 shrink-0">
                      <span className="text-5xl font-bold text-muted-foreground/20 font-caslon">{step.number}</span>
                      <div className="w-14 h-14 rounded-2xl bg-foreground flex items-center justify-center">
                        <Icon className="w-7 h-7 text-background" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-caslon text-xl font-bold text-foreground mb-3">{step.title}</h3>
                      <p className="text-muted-foreground leading-relaxed mb-2">{step.text}</p>
                      <p className="text-sm text-foreground font-medium">→ {step.detail}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Warum Stein + Checkliste */}
        <section className="py-16 md:py-24 lg:py-32 bg-foreground text-background">
          <div className="container mx-auto">
            <div className="grid lg:grid-cols-2 gap-16">
              {/* Warum Stein */}
              <div>
                <span className="text-sm font-semibold tracking-wider uppercase text-background/50 mb-4 block">
                  Expertise
                </span>
                <h2 className="font-caslon text-3xl md:text-4xl font-bold mb-6">
                  Expertise für Altbau & Modernisierung im Kreis Herford
                </h2>
                <p className="text-background/80 leading-relaxed text-lg">
                  „Wir lieben die Herausforderung alter Bausubstanz. Ob Fachwerkhäuser in der Herforder 
                  Innenstadt oder Siedlungshäuser aus den 60ern – wir wissen, wie man historische Substanz 
                  mit moderner Technik (WLS 022 Dämmung, mineralische Putze) zukunftssicher macht."
                </p>
              </div>

              {/* Checkliste */}
              <div>
                <h3 className="font-caslon text-2xl font-bold mb-8 text-background">
                  Was wir bei Ihrer Sanierung prüfen
                </h3>
                <div className="space-y-6">
                  {checklist.map((item, i) => (
                    <div key={i} className="flex gap-4 items-start">
                      <div className="w-10 h-10 rounded-xl bg-background/10 flex items-center justify-center shrink-0">
                        <Check className="w-5 h-5 text-background" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-background mb-1">{item.title}</h4>
                        <p className="text-background/60 text-sm">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 lg:py-32 bg-muted/30">
          <div className="container mx-auto max-w-3xl">
            <div className="text-center mb-16">
              <span className="text-sm font-semibold tracking-wider uppercase text-muted-foreground mb-4 block">
                Häufige Fragen
              </span>
              <h2 className="font-caslon text-3xl md:text-4xl font-bold text-foreground">
                Wissenswertes zur Komplettrenovierung
              </h2>
            </div>
            
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`} 
                  className="bg-background border border-foreground/10 rounded-2xl px-6"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-6">
                    <span className="font-semibold pr-4">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 lg:py-32 bg-foreground">
          <div className="container mx-auto text-center max-w-3xl">
            <h2 className="font-caslon text-3xl md:text-4xl font-bold text-background mb-6">
              Starten Sie jetzt Ihre Haus- oder Wohnungssanierung
            </h2>
            <p className="text-lg text-background/80 mb-10 leading-relaxed">
              Vereinbaren Sie einen Vor-Ort-Termin in Herford. Wir analysieren den Bestand und 
              erstellen Ihnen einen detaillierten Fahrplan.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-background text-foreground hover:bg-background/90 font-semibold px-10 py-7 text-base rounded-2xl"
                onClick={() => document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Jetzt Beratungstermin buchen
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <a href="tel:+4952213832130">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-background text-background hover:bg-background/10 font-semibold px-10 py-7 text-base rounded-2xl">
                  <Phone className="mr-2 h-5 w-5" />
                  05221 / 3832130
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Kontaktformular */}
        <Contact />
      </main>
      
      <Footer />
    </div>
  );
};

export default Innenarbeiten;
